import styled from 'styled-components';
import type { KsPackage, KsPackageIssue } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import {
  decisionLabel,
  decisionTone,
  issueKindLabel,
  severityTone,
} from '@shared/lib/smetaLabels';

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoIssuesMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const IssueKfoMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const DecisionBtns = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }
`;

export interface KsPackageIssuesTabProps {
  pkg: KsPackage;
  issues: KsPackageIssue[];
  counts?: { error: number; warning: number; blocking: number };
  canWrite: boolean;
  frozen: boolean;
  annotating: boolean;
  onDecision: (issueId: string, decision: 'accepted' | 'disputed' | 'rejected') => void;
}

export function KsPackageIssuesTab({
  pkg,
  issues,
  counts,
  canWrite,
  frozen,
  annotating,
  onDecision,
}: KsPackageIssuesTabProps) {
  const packageIssues = issues.filter((i) => !i.line_id);
  const decisionTitle = !canWrite
    ? 'Только чтение'
    : frozen
      ? 'Пакет выпущен — решения по находкам заморожены'
      : undefined;

  return (
    <>
      <TableTitle>
        Находки сборки{' '}
        <Badge $tone="danger">{counts ? `ошибок ${counts.error}` : 'ошибок 0'}</Badge>
        <Badge $tone="warning">
          {counts ? `предупреждений ${counts.warning}` : 'предупреждений 0'}
        </Badge>
        {counts && counts.blocking > 0 && (
          <Badge $tone="danger">без решения {counts.blocking}</Badge>
        )}
      </TableTitle>
      <Hint>
        Неразобранные находки уровня «ошибка» держат выпуск: комплект под подпись не
        должен уехать заказчику с расхождением, о котором знал только сборщик.
      </Hint>
      {issues.length === 0 ? (
        <NoIssuesMuted>
          {pkg.rebuild_no === 0
            ? 'Находок нет — пакет ещё не собирался.'
            : pkg.status === 'build_failed'
              ? 'Находок нет, но и разбирать нечего: последняя сборка упала, её ' +
                'находки откатились, а находки прошлых сборок сняты. Почините ' +
                'причину (текст ошибки — в шапке пакета) и соберите заново.'
              : 'Находок нет.'}
        </NoIssuesMuted>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Важность</th>
                <th>Тип</th>
                <th>Сообщение</th>
                <th>Уровень</th>
                <th>Решение</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i) => {
                const isKfoGate = i.anchor === 'kfo_aosr_gate';
                return (
                  <tr key={i.id}>
                    <td>
                      <Badge $tone={severityTone(i.severity)}>{i.severity}</Badge>
                    </td>
                    <td>{issueKindLabel(i.kind)}</td>
                    <td>
                      <div>{i.message}</div>
                      {isKfoGate && i.decision !== 'accepted' && (
                        <IssueKfoMuted>
                          Снимается только при наличии подписанного АОСР в ПТО или
                          принятой заявки RFI в КФО.
                        </IssueKfoMuted>
                      )}
                    </td>
                    <td>{i.line_id ? 'строка' : i.act_id ? 'акт' : 'пакет'}</td>
                    <td>
                      {i.decision !== 'pending' ? (
                        <Badge $tone={decisionTone(i.decision)}>
                          {decisionLabel(i.decision)}
                        </Badge>
                      ) : (
                        <DecisionBtns>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            type="button"
                            disabled={annotating || !canWrite || frozen}
                            title={
                              decisionTitle ??
                              (isKfoGate
                                ? 'Проверит наличие подписанного АОСР в ПТО или принятого RFI в КФО'
                                : undefined)
                            }
                            onClick={() => onDecision(i.id, 'accepted')}
                          >
                            {isKfoGate ? 'Проверить АОСР / RFI' : 'Принять'}
                          </Button>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            type="button"
                            disabled={annotating || !canWrite || frozen}
                            title={decisionTitle}
                            onClick={() => onDecision(i.id, 'disputed')}
                          >
                            Спорно
                          </Button>
                          {!isKfoGate && (
                            <Button
                              $variant="ghost"
                              $size="sm"
                              type="button"
                              disabled={annotating || !canWrite || frozen}
                              title={decisionTitle}
                              onClick={() => onDecision(i.id, 'rejected')}
                            >
                              Отклонить
                            </Button>
                          )}
                        </DecisionBtns>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
      {packageIssues.length > 0 && (
        <Hint>
          Находок вне строк: {packageIssues.length} — они относятся к пакету или акту
          целиком и в таблице строк не подсвечены.
        </Hint>
      )}
    </>
  );
}
